import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { useToast } from '../context/ToastContext';

const ServerDetails = () => {
    const { domain } = useParams();
    const navigate = useNavigate();
    const { error: toastError } = useToast();
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [pingData, setPingData] = useState(null);
    const [pingLoading, setPingLoading] = useState(false);

    const [pingError, setPingError] = useState(false);

    const [emailSecData, setEmailSecData] = useState(null);
    const [emailSecLoading, setEmailSecLoading] = useState(false);

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const res = await api.get(`/api/server-details/${domain}`);
                setDetails(res.data);
            } catch (err) {
                console.error("Error fetching server details:", err);
                setError(err.message || "Failed to load server details.");
                // toastError("Failed to load server details."); // Avoid double error toast if using full screen
            } finally {
                setLoading(false);
            }
        };

        const fetchEmailSecurity = async () => {
            setEmailSecLoading(true);
            try {
                const res = await api.get(`/api/server-details/email-security/${domain}`);
                setEmailSecData(res.data);
            } catch (err) {
                console.error("Email security fetch failed:", err);
            } finally {
                setEmailSecLoading(false);
            }
        };

        if (domain) {
            fetchDetails();
            fetchEmailSecurity();
        }
    }, [domain, toastError]);

    // Auto-ping when IP is available
    useEffect(() => {
        const performPing = async () => {
            if (!details?.ip) return;

            setPingLoading(true);
            setPingData(null);
            setPingError(false);

            try {
                const res = await api.get(`/api/server-details/ping/${details.ip}`);
                setPingData(res.data);
            } catch (err) {
                console.error("Ping failed:", err);
                setPingError(true);
            } finally {
                setPingLoading(false);
            }
        };

        if (details?.ip) {
            performPing();
        }
    }, [details?.ip]); // Run only when IP changes using optional chaining safe dependency? Better use details.

    const ipToDecimal = (ip) => {
        if (!ip) return 0;
        return ip.split('.').reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0) >>> 0;
    };

    const [showBlacklistModal, setShowBlacklistModal] = useState(false);
    const [blacklistData, setBlacklistData] = useState(null);
    const [blacklistLoading, setBlacklistLoading] = useState(false);

    const handleBlacklistCheck = async () => {
        if (!details?.ip) return;

        setShowBlacklistModal(true);
        if (blacklistData) return; // Don't re-fetch if already have data

        setBlacklistLoading(true);
        try {
            const res = await api.get(`/api/server-details/blacklist/${details.ip}`);
            setBlacklistData(res.data);
        } catch (err) {
            console.error("Blacklist check failed:", err);
        } finally {
            setBlacklistLoading(false);
        }
    };

    return (
        <div className="container bg-light min-vh-100 py-4 position-relative">
            {/* Blacklist Modal */}
            {showBlacklistModal && (
                <div className="modal fade show d-block bg-[rgba(0,0,0,0.5)]" tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content shadow">
                            <div className="modal-header bg-danger text-white">
                                <h5 className="modal-title">
                                    <i className="las la-shield-alt me-2"></i>
                                    Blacklist Status
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowBlacklistModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-3">Checking IP: <strong>{details?.ip}</strong></p>

                                {blacklistLoading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-danger" role="status"></div>
                                        <p className="mt-2 text-muted">Querying RBLs...</p>
                                    </div>
                                ) : blacklistData ? (
                                    <>
                                        <div className={`alert ${blacklistData.isListed ? 'alert-danger' : 'alert-success'} d-flex align-items-center`}>
                                            <i className={`las ${blacklistData.isListed ? 'la-exclamation-triangle' : 'la-check-circle'} fs-4 me-2`}></i>
                                            <div>
                                                <strong>{blacklistData.isListed ? 'Listed in Blacklists!' : 'Clean'}</strong>
                                                <div className="small">{blacklistData.isListed ? 'This IP is listed in one or more real-time blackhole lists.' : 'This IP is not listed in the queried RBLs.'}</div>
                                            </div>
                                        </div>

                                        <ul className="list-group">
                                            {blacklistData.results?.map((rbl, idx) => (
                                                <li key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                                                    <span>{rbl.rbl}</span>
                                                    {rbl.listed ? (
                                                        <span className="badge bg-danger rounded-pill">Listed</span>
                                                    ) : (
                                                        <span className="badge bg-success rounded-pill">Clean</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                ) : (
                                    <p className="text-danger">Failed to fetch blacklist data.</p>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowBlacklistModal(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-0">
                                <h4 className="mb-0">
                                    Server Details for: <span className="text-primary">{domain}</span>
                                </h4>
                                <button className="btn btn-outline-dark btn-sm" onClick={() => navigate('/scans')}>
                                    ← Back to Scans
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : details ? (
                <div className="row g-4">
                    <div className="col-md-4">
                        <div className="card shadow-sm h-100">
                            <div className="card-header bg-primary text-white">
                                <h5 className="card-title mb-0">
                                    <i className="las la-network-wired me-2"></i>
                                    Network Information
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="mb-2">
                                    <label className="text-muted small d-block mb-1">IP Address</label>
                                    <span className="fw-bold text-dark fs-5">{details.ip}</span>
                                </div>

                                {pingLoading && (
                                    <div className="mt-3 text-center text-muted small">
                                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                                        Pinging server...
                                    </div>
                                )}

                                {!pingLoading && !pingData && !pingError && (
                                    <div className="mt-3 text-center text-muted small fst-italic">
                                        Waiting for ping stats...
                                    </div>
                                )}

                                {pingError && (
                                    <div className="mt-3 text-danger small">
                                        <i className="las la-exclamation-circle me-1"></i>
                                        Ping failed
                                    </div>
                                )}

                                {pingData && (
                                    <div className="mt-3 p-2 bg-light rounded border small">
                                        <div className="d-flex justify-content-between mb-1">
                                            <span>Packet Loss:</span>
                                            <span className={`fw-bold ${pingData.loss === '0' ? 'text-success' : 'text-danger'}`}>{pingData.loss}%</span>
                                        </div>
                                        {pingData.stats && (
                                            <>
                                                <div className="d-flex justify-content-between">
                                                    <span>Min Latency:</span>
                                                    <span>{pingData.stats[0]} ms</span>
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                    <span>Avg Latency:</span>
                                                    <span className="fw-bold">{pingData.stats[1]} ms</span>
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                    <span>Max Latency:</span>
                                                    <span>{pingData.stats[2]} ms</span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Hosting Information (New Card) */}
                    <div className="col-md-4">
                        <div className="card shadow-sm h-100">
                            <div className="card-header bg-success text-white">
                                <h5 className="card-title mb-0">
                                    <i className="las la-server me-2"></i>
                                    Hosting Details
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="text-muted small d-block mb-1">Hosting Provider</label>
                                    <span className="fw-bold text-dark">{details.isp}</span>
                                </div>
                                <div className="mb-3">
                                    <label className="text-muted small d-block mb-1">Organization</label>
                                    <span className="text-dark">{details.org || 'N/A'}</span>
                                </div>
                                <div className="mb-3">
                                    <label className="text-muted small d-block mb-1">ASN</label>
                                    <span className="text-dark bg-light px-2 py-1 rounded border">{details.as}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="card shadow-sm h-100">
                            <div className="card-header bg-info text-white">
                                <h5 className="card-title mb-0">
                                    <i className="las la-map-marker-alt me-2"></i>
                                    Location Details
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="text-muted small d-block mb-1">Country</label>
                                    <span className="fw-bold text-dark d-flex align-items-center">
                                        {details.countryCode && <img src={`https://flagcdn.com/24x18/${details.countryCode.toLowerCase()}.png`} alt={details.country} className="me-2" />}
                                        {details.country}
                                    </span>
                                </div>
                                <div className="row">
                                    <div className="col-6 mb-3">
                                        <label className="text-muted small d-block mb-1">City</label>
                                        <span className="text-dark">{details.city}</span>
                                    </div>
                                    <div className="col-6 mb-3">
                                        <label className="text-muted small d-block mb-1">Region</label>
                                        <span className="text-dark">{details.region}</span>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="text-muted small d-block mb-1">Timezone</label>
                                    <span className="text-dark font-monospace small">{details.timezone}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : error ? (
                <div className="d-flex align-items-center justify-content-center min-h-[60vh]">
                    <div className="text-center p-5">
                        <i className="las la-server text-danger text-[5rem]"></i>
                        <h2 className="text-danger mt-4 mb-3">Server Lookup Failed</h2>
                        <p className="lead mb-4 text-muted max-w-[600px] mx-auto">
                            Could not retrieve server information for <strong>{domain}</strong>.
                        </p>
                        <div className="alert alert-light border d-inline-block text-start max-w-[500px]">
                            <p className="mb-2 fw-bold text-danger small"><i className="las la-bug me-1"></i>Error Details:</p>
                            <code className="text-danger small d-block mb-3">{error}</code>
                        </div>
                        <div className="mt-4">
                            <button onClick={() => window.location.reload()} className="btn btn-primary me-2">
                                <i className="las la-sync me-2"></i>Retry
                            </button>
                            <button onClick={() => navigate('/scans')} className="btn btn-outline-secondary">
                                <i className="las la-arrow-left me-2"></i>Back to Scans
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="text-center py-5 text-muted">
                    <i className="las la-exclamation-circle fa-2x mb-2"></i>
                    <p>No details found for this domain.</p>
                </div>
            )}

            {/* Extended Details & Map Row */}
            {details && details.lat && details.lon && (
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card shadow-sm">
                            <div className="card-header bg-dark text-white">
                                <h5 className="card-title mb-0">
                                    <i className="las la-globe-americas me-2"></i>
                                    Geographic Location & Extended Details
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-4">
                                        <h6 className="border-bottom pb-2 mb-3">Extended Information</h6>

                                        <div className="mb-3">
                                            <label className="text-muted small d-block mb-1">Decimal IP</label>
                                            <span className="fw-bold text-dark font-monospace">{ipToDecimal(details.ip)}</span>
                                        </div>

                                        <div className="mb-3">
                                            <label className="text-muted small d-block mb-1">Hostname</label>
                                            <div className="text-dark text-break small font-monospace bg-light p-2 rounded">
                                                {details.hostname || 'N/A'}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-6 mb-3">
                                                <label className="text-muted small d-block mb-1">Latitude</label>
                                                <span className="text-dark font-monospace">{details.lat}</span>
                                            </div>
                                            <div className="col-6 mb-3">
                                                <label className="text-muted small d-block mb-1">Longitude</label>
                                                <span className="text-dark font-monospace">{details.lon}</span>
                                            </div>
                                        </div>

                                        <button className="btn btn-sm btn-outline-danger w-100" onClick={handleBlacklistCheck}>
                                            <i className="las la-shield-alt me-1"></i> Check Blacklist Status
                                        </button>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="ratio ratio-21x9 rounded overflow-hidden border">
                                            <iframe
                                                title="Server Location"
                                                width="100%"
                                                height="100%"
                                                src={`https://www.openstreetmap.org/export/embed.html?bbox=${details.lon - 0.1},${details.lat - 0.1},${details.lon + 0.1},${details.lat + 0.1}&layer=mapnik&marker=${details.lat},${details.lon}`}
                                                style={{ border: 0 }}
                                            ></iframe>
                                        </div>
                                        <small className="text-muted mt-2 d-block text-end">
                                            <a href={`https://www.openstreetmap.org/?mlat=${details.lat}&mlon=${details.lon}#map=12/${details.lat}/${details.lon}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-muted">
                                                <i className="las la-external-link-alt me-1"></i>View Larger Map
                                            </a>
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Email Security Row */}
            {emailSecData && (
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card shadow-sm">
                            <div className="card-header bg-secondary text-white">
                                <h5 className="card-title mb-0">
                                    <i className="las la-envelope-open-text me-2"></i>
                                    Email Security Configuration
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <h6 className="border-bottom pb-2 mb-3">SPF Record</h6>
                                        {emailSecLoading ? (
                                            <div className="spinner-border spinner-border-sm text-secondary" role="status"></div>
                                        ) : emailSecData.spf ? (
                                            <div className="alert alert-success py-2 small">
                                                <i className="las la-check-circle me-1"></i> Found
                                                <div className="mt-1 font-monospace text-break bg-white p-1 rounded border">
                                                    {emailSecData.spf}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="alert alert-warning py-2 small">
                                                <i className="las la-exclamation-triangle me-1"></i> Not Found
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <h6 className="border-bottom pb-2 mb-3">DMARC Record</h6>
                                        {emailSecLoading ? (
                                            <div className="spinner-border spinner-border-sm text-secondary" role="status"></div>
                                        ) : emailSecData.dmarc ? (
                                            <div className="alert alert-success py-2 small">
                                                <i className="las la-check-circle me-1"></i> Found
                                                <div className="mt-1 font-monospace text-break bg-white p-1 rounded border">
                                                    {emailSecData.dmarc}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="alert alert-warning py-2 small">
                                                <i className="las la-exclamation-triangle me-1"></i> Not Found
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <h6 className="border-bottom pb-2 mb-3">MX Records</h6>
                                        {emailSecLoading ? (
                                            <div className="spinner-border spinner-border-sm text-secondary" role="status"></div>
                                        ) : emailSecData.mx && emailSecData.mx.length > 0 ? (
                                            <ul className="list-group small">
                                                {emailSecData.mx.map((mx, i) => (
                                                    <li key={i} className="list-group-item d-flex justify-content-between">
                                                        <span className="text-truncate me-2" title={mx.exchange}>{mx.exchange}</span>
                                                        <span className="badge bg-secondary rounded-pill">{mx.priority}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="alert alert-danger py-2 small">
                                                <i className="las la-times-circle me-1"></i> No MX Records Found
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServerDetails;
